import React, { useEffect, useRef, useState } from "react";

import { formatOrderDate } from "../../../utils/orders";
import { cardTotal, isPeppolExpired, pastOrderDate, pastOrderId } from "../../../utils/pastOrders";
import { Icon } from "./icons";

/** Alerte quand le panier n'a pas pu être rempli. */
export const reorderFailed = (t) => ({
  icon: "alert-circle",
  tone: "danger",
  title: t("pastOrderCard.orderAgain"),
  message: t("We couldn't fill your cart with this order. Try again in a moment.", { ns: "translation" }),
  confirm: "OK",
});

export const MiniStars = ({ value }) => (
  <span className="wosh-po__stars" aria-label={`${value}/5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Icon key={n} name="star" className={n <= value ? "wosh-po__star--on" : undefined} />
    ))}
  </span>
);

/**
 * Hook « commander à nouveau » : repos → chargement, puis `onDone` (le
 * catalogue, panier rempli) ou `onError` et retour au repos.
 */
export function useReorder(run, onDone, onError) {
  const [state, setState] = useState("idle");
  const mounted = useRef(true);

  useEffect(() => () => (mounted.current = false), []);

  const trigger = async () => {
    if (state !== "idle") return;
    setState("loading");
    try {
      await run();
    } catch (error) {
      if (mounted.current) setState("idle");
      onError(error);
      return;
    }
    onDone();
  };

  return [state, trigger];
}

/**
 * Carte de l'onglet Passées (PastOrderCard.dc.html, PastOrderCard.tsx du
 * mobile). Toute la carte ouvre le détail ; les boutons n'y propagent pas.
 */
export default function PastOrderCard({
  order,
  t,
  lang,
  now,
  onOpen,
  onReorder,
  onOpenCart,
  onReorderError,
  onReceipt,
  onReceiptError,
  onPeppol,
}) {
  const [reorderState, reorderNow] = useReorder(() => onReorder(order), onOpenCart, onReorderError);
  const [receipt, setReceipt] = useState("idle");

  const date = formatOrderDate(pastOrderDate(order), lang) || t("pastOrderCard.recentOrder");
  const total = cardTotal(order);
  const peppolExpired = isPeppolExpired(order, now);
  const stop = (fn) => (event) => {
    event.stopPropagation();
    fn();
  };

  const downloadReceipt = async () => {
    if (receipt === "loading") return;
    setReceipt("loading");
    try {
      await onReceipt(order);
      setReceipt("idle");
    } catch (error) {
      // L'icône reste en erreur jusqu'à la fermeture de l'alerte, comme sur mobile.
      setReceipt("error");
      onReceiptError(() => setReceipt("idle"));
    }
  };

  return (
    <article
      className="wosh-po"
      role="link"
      tabIndex={0}
      onClick={() => onOpen(order)}
      onKeyDown={(event) => event.key === "Enter" && event.target === event.currentTarget && onOpen(order)}
    >
      <div className="wosh-po__top">
        <div className="wosh-po__row">
          <span className="wosh-po__id">{pastOrderId(order)}</span>
          <span className="wosh-po__pill">
            <span className="wosh-po__pill-dot" />
            {t("pastOrderCard.delivered")}
          </span>
        </div>
        <div className="wosh-po__row wosh-po__row--base">
          <span className="wosh-po__date">{date}</span>
          {total && <span className="wosh-po__total">{total}</span>}
        </div>
        {order.review ? (
          <MiniStars value={order.review.stars} />
        ) : (
          <span className="wosh-po__rate">
            {t("pastOrderCard.tapToRate").replace(/\s*★\s*$/, "")}
            <Icon name="star" />
          </span>
        )}
      </div>

      <button
        type="button"
        className={`wosh-po__again wosh-po__again--${reorderState}`}
        disabled={reorderState === "loading"}
        onClick={stop(reorderNow)}
      >
        {reorderState === "loading" ? <span className="wosh-po__spin" /> : <Icon name="refresh" />}
        {reorderState === "loading" ? t("pastOrderCard.addingToCart") : t("pastOrderCard.orderAgain")}
      </button>

      <div className="wosh-po__links">
        <button
          type="button"
          className={`wosh-po__link${receipt === "error" ? " wosh-po__link--error" : ""}`}
          disabled={receipt === "loading"}
          onClick={stop(downloadReceipt)}
        >
          {receipt === "loading" ? (
            <span className="wosh-po__spin wosh-po__spin--small" />
          ) : (
            <Icon name={receipt === "error" ? "alert-circle" : "download-outline"} />
          )}
          {t("pastOrderCard.downloadReceipt")}
        </button>
        <span className="wosh-po__divider" />
        <button
          type="button"
          className={`wosh-po__link${peppolExpired ? " wosh-po__link--off" : ""}`}
          onClick={stop(() => onPeppol(order, peppolExpired))}
        >
          <Icon name="document-text-outline" />
          {t("pastOrderCard.peppolInvoice")}
        </button>
      </div>
    </article>
  );
}
